import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ImageUpload } from '@/components/shared/ImageUpload';
import { Slider } from '@/components/ui/slider';
import { Pencil, Trash2, Loader2, Megaphone } from 'lucide-react';
import { toast } from 'sonner';
import { adminApi } from '@/services/api';
import { bannerScrollSpeedLabel, clampBannerScrollSpeed } from '@/utils/bannerScrollSpeed';
import { normalizeExternalUrl } from '@/utils/externalUrl';
import { LoadingSpinner, EmptyState, StatusBadge } from './shared';

const PLATFORM_STORE_ID = 'byggfynd-platform';

export const AdsSection = ({
  banners,
  bannerSettings,
  setBannerSettings,
  loading,
  filter,
  setFilter,
  onApprove,
  onReject,
  onMarkPaid,
  onEdit,
  onDelete,
  onSaveSettings,
  savingBanner,
  onRefresh,
}) => {
  const [newTitle, setNewTitle] = useState('');
  const [newImageUrl, setNewImageUrl] = useState('');
  const [newLinkUrl, setNewLinkUrl] = useState('');
  const [creating, setCreating] = useState(false);

  const logoW = bannerSettings?.recommended_width || 320;
  const logoH = bannerSettings?.recommended_height || 80;
  const scrollSpeed = clampBannerScrollSpeed(bannerSettings?.scroll_speed ?? 3);

  const handleCreateEditorial = async (e) => {
    e.preventDefault();
    const title = newTitle.trim();
    const image_url = newImageUrl.trim();
    if (!title || !image_url) {
      toast.error('Företagsnamn och logotyp krävs.');
      return;
    }
    setCreating(true);
    try {
      await adminApi.createBanner({
        title,
        image_url,
        link_url: normalizeExternalUrl(newLinkUrl),
      });
      toast.success('Logotyp tillagd — syns i rullistan på startsidan.');
      setNewTitle('');
      setNewImageUrl('');
      setNewLinkUrl('');
      onRefresh?.();
    } catch {
      toast.error('Kunde inte skapa banner.');
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-ads">
      {bannerSettings && (
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h3 className="font-bold text-[#0F172A] mb-4">Inställningar för partnerbanner</h3>

          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6 pb-6 border-b border-gray-100 max-w-2xl">
            <div>
              <Label htmlFor="banner-enabled">Visa rullande partnerbanner på startsidan</Label>
              <p className="text-xs text-gray-500 mt-1">Avstängd döljer hela sektionen (loggor och reservtext).</p>
            </div>
            <Switch
              id="banner-enabled"
              checked={bannerSettings.is_enabled !== false}
              onCheckedChange={(v) => setBannerSettings({ ...bannerSettings, is_enabled: v })}
            />
          </div>

          <div className="mb-6 max-w-2xl">
            <Label htmlFor="fallback-scroll">Reservtext (rullande)</Label>
            <Textarea
              id="fallback-scroll"
              rows={3}
              placeholder="Visas när det inte finns några aktiva logotyp-annonser. T.ex. erbjudanden eller uppmaning att kontakta er för synlighet."
              value={bannerSettings.fallback_scroll_text ?? ''}
              onChange={(e) => setBannerSettings({ ...bannerSettings, fallback_scroll_text: e.target.value })}
              className="mt-1"
            />
            <p className="text-xs text-gray-500 mt-1">
              Lämna tom om inget ska rulla när listan med loggor är tom.
            </p>
          </div>

          <div className="mb-4">
            <Label>Rubrik ovanför bannern</Label>
            <Input
              type="text"
              value={bannerSettings.banner_title ?? ''}
              onChange={(e) => setBannerSettings({ ...bannerSettings, banner_title: e.target.value })}
              placeholder="T.ex. Våra samarbetspartners"
              className="mt-1 max-w-md"
            />
            <p className="text-xs text-gray-500 mt-1">Lämna tomt för att dölja rubriken</p>
          </div>

          <div className="mb-6 max-w-xl pb-6 border-b border-gray-100">
            <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
              <Label htmlFor="banner-scroll-speed">Rullhastighet (loggor på startsidan)</Label>
              <span className="text-sm font-semibold text-[#003B73]">
                {scrollSpeed} — {bannerScrollSpeedLabel(scrollSpeed)}
              </span>
            </div>
            <Slider
              id="banner-scroll-speed"
              min={1}
              max={10}
              step={1}
              value={[scrollSpeed]}
              onValueChange={([value]) =>
                setBannerSettings({ ...bannerSettings, scroll_speed: value })
              }
              className="py-2"
            />
            <div className="flex justify-between text-[10px] text-gray-400 mt-1 px-0.5">
              <span>1 Långsam</span>
              <span>5 Normal</span>
              <span>10 Snabb</span>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              Dra reglaget och klicka <strong>Spara inställningar</strong> nedan — då uppdateras hastigheten på startsidan.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <Label>Höjd rullist desktop (px)</Label>
              <Input type="number" value={bannerSettings.banner_height_desktop || 120} onChange={(e) => setBannerSettings({ ...bannerSettings, banner_height_desktop: parseInt(e.target.value, 10) })} className="mt-1" />
            </div>
            <div>
              <Label>Höjd rullist mobil (px)</Label>
              <Input type="number" value={bannerSettings.banner_height_mobile || 80} onChange={(e) => setBannerSettings({ ...bannerSettings, banner_height_mobile: parseInt(e.target.value, 10) })} className="mt-1" />
            </div>
            <div>
              <Label>Pris per logotyp (kr/mån)</Label>
              <Input type="number" value={bannerSettings.price_per_ad || 499} onChange={(e) => setBannerSettings({ ...bannerSettings, price_per_ad: parseFloat(e.target.value) })} className="mt-1" />
            </div>
          </div>
          <div className="mt-4 p-4 rounded-xl bg-[#F8FAFC] border border-gray-100 max-w-2xl">
            <p className="text-sm font-semibold text-[#0F172A] mb-1">Rekommenderad logotyp för annonsörer</p>
            <p className="text-sm text-gray-600">
              <strong>{logoW} × {logoH} px</strong> — horisontell logotyp, PNG eller WebP med transparent bakgrund.
              Bredden kan variera (t.ex. 240–400 px) så länge höjden är ca {logoH} px; loggan skalas automatiskt i rullistan på mobil och desktop.
            </p>
            <div className="grid sm:grid-cols-2 gap-3 mt-3">
              <div>
                <Label className="text-xs text-gray-500">Rek. bredd (px)</Label>
                <Input type="number" value={logoW} onChange={(e) => setBannerSettings({ ...bannerSettings, recommended_width: parseInt(e.target.value, 10) })} className="mt-1" />
              </div>
              <div>
                <Label className="text-xs text-gray-500">Rek. höjd (px)</Label>
                <Input type="number" value={logoH} onChange={(e) => setBannerSettings({ ...bannerSettings, recommended_height: parseInt(e.target.value, 10) })} className="mt-1" />
              </div>
            </div>
          </div>
          <Button className="mt-4 bg-[#003B73]" onClick={onSaveSettings} disabled={savingBanner}>
            {savingBanner && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
            Spara inställningar
          </Button>
        </div>
      )}

      <div className="bg-white rounded-lg shadow-sm p-6 border border-[#003B73]/15">
        <div className="flex items-start gap-3 mb-4">
          <div className="w-10 h-10 rounded-lg bg-[#003B73]/10 flex items-center justify-center flex-shrink-0">
            <Megaphone className="h-5 w-5 text-[#003B73]" />
          </div>
          <div>
            <h3 className="font-bold text-[#0F172A]">Lägg till rullande logotyp</h3>
            <p className="text-sm text-gray-600 mt-1">
              Företagsloggor som rullar på startsidan (reklam / synlighet). Butiker kan inte lägga upp egna — de kontaktar er.
            </p>
          </div>
        </div>
        <form onSubmit={handleCreateEditorial} className="grid md:grid-cols-2 gap-4 max-w-4xl">
          <div className="md:col-span-2">
            <Label>Företagsnamn (internt)</Label>
            <Input value={newTitle} onChange={(e) => setNewTitle(e.target.value)} placeholder="T.ex. Byggmax, XL-BYGG …" className="mt-1" />
          </div>
          <div className="md:col-span-2">
            <Label>Logotyp</Label>
            <p className="text-xs text-gray-500 mt-1 mb-2">
              Rekommenderat: {logoW}×{logoH} px, PNG/WebP, transparent bakgrund.
            </p>
            <Input value={newImageUrl} onChange={(e) => setNewImageUrl(e.target.value)} placeholder="https://… eller ladda upp nedan" className="mb-2" />
            <ImageUpload
              folder="banners"
              currentImage={newImageUrl}
              onUpload={(url) => setNewImageUrl(url || '')}
              aspectRatio="aspect-[4/1]"
              uploadLabel="Ladda upp logotyp"
            />
          </div>
          <div className="md:col-span-2">
            <Label>Webbplats vid klick</Label>
            <Input
              value={newLinkUrl}
              onChange={(e) => setNewLinkUrl(e.target.value)}
              placeholder="www.abkarlhedin.se"
              className="mt-1"
            />
            <p className="text-xs text-gray-500 mt-1">
              Besökaren öppnar länken i ny flik. Du kan skriva med eller utan https://
            </p>
          </div>
          <div className="md:col-span-2">
            <Button type="submit" className="bg-[#003B73]" disabled={creating || !newImageUrl.trim()}>
              {creating && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Lägg till i rullistan
            </Button>
          </div>
        </form>
      </div>

      <div className="flex justify-between items-center flex-wrap gap-2">
        <h3 className="font-bold">Alla rullande logotyper</h3>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-48"><SelectValue placeholder="Filtrera" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alla</SelectItem>
            <SelectItem value="pending">Väntande</SelectItem>
            <SelectItem value="approved">Godkända</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {loading ? <LoadingSpinner /> : banners.length > 0 ? (
        <div className="grid md:grid-cols-2 gap-4">
          {banners.map((banner) => {
            const isPlatform = banner.store_id === PLATFORM_STORE_ID;
            return (
              <div key={banner.id} className="bg-white rounded-lg shadow-sm overflow-hidden">
                <div className="aspect-[4/1] bg-[#0F172A] flex items-center justify-center p-4">
                  <img src={banner.image_url} alt={banner.title} className="max-h-full max-w-full object-contain" />
                </div>
                <div className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-bold">{banner.title}</h4>
                    <StatusBadge status={banner.status} />
                  </div>
                  <p className="text-sm text-gray-500 mb-2">
                    {isPlatform ? 'ByggFynd (redaktionellt)' : `Butik: ${banner.store_name || 'Okänd'}`}
                  </p>
                  {banner.link_url ? (
                    <p className="text-xs text-[#003B73] mb-2 truncate" title={banner.link_url}>
                      Länk: {banner.link_url}
                    </p>
                  ) : (
                    <p className="text-xs text-amber-700 mb-2">Ingen webblänk — klicka Redigera för att lägga till</p>
                  )}
                  {!isPlatform && (
                    <div className="flex items-center gap-2 text-sm mb-3">
                      <span className={`px-2 py-0.5 rounded ${banner.payment_status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                        {banner.payment_status === 'paid' ? 'Betald' : 'Ej betald'}
                      </span>
                    </div>
                  )}
                  <div className="flex gap-2 flex-wrap">
                    {!isPlatform && banner.status === 'pending' && (
                      <>
                        <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={() => onApprove(banner.id)}>Godkänn</Button>
                        <Button size="sm" variant="destructive" onClick={() => onReject(banner.id)}>Avvisa</Button>
                      </>
                    )}
                    {!isPlatform && banner.status === 'approved' && banner.payment_status !== 'paid' && (
                      <Button size="sm" variant="outline" onClick={() => onMarkPaid(banner.id)}>Markera betald</Button>
                    )}
                    <Button size="sm" variant="outline" onClick={() => onEdit(banner)}>
                      <Pencil className="h-4 w-4 mr-1" /> Redigera
                    </Button>
                    <Button size="sm" variant="ghost" className="text-red-600" onClick={() => onDelete(banner.id)}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : <EmptyState message="Inga logotyper ännu — ladda upp en ovan eller spara reservtext." />}
    </div>
  );
};
